import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core'
import { PageMetadata } from '@ftr/contracts/type/shared'
import { ButtonColor, ButtonComponent, FloatingTab, IconComponent } from '@ftr/foundation'
import { Observable } from 'rxjs'
import { RadioListFilter } from '../../complex-filter/RadioListFilter'
import { PaginationComponent } from '../../pagination'
import { SortDirectionText } from '../../sort-direction'
import { ComplexListSortComponent, SortByDropdownHeaderSize } from '../sort/complex-list-sort.component'
import { ComplexListStatusTypeComponent } from '../status-type/complex-list-status-type.component'

enum BadgeType {
  Primary = 'primary',
  Secondary = 'secondary',
}

export interface ComplexFilterListHeaderSectionConfiguration {
  floatingTabsVisibility?: ComplexListHeaderFloatingTabsVisibilityOptions
  filterButtonVisibility?: ComplexListHeaderFilterButtonVisibilityOptions
  sortByDropdownHeaderSize: SortByDropdownHeaderSize
}

export enum ComplexListHeaderFilterButtonVisibilityOptions {
  MobileOnly = 'mobile-only',
  Always = 'always',
}

export enum ComplexListHeaderFloatingTabsVisibilityOptions {
  TabletAndDesktop = 'tablet-and-desktop',
  DesktopOnly = 'desktop-only',
}

const DEFAULT_HEADER_SECTION_CONFIG: ComplexFilterListHeaderSectionConfiguration = {
  floatingTabsVisibility: ComplexListHeaderFloatingTabsVisibilityOptions.DesktopOnly,
  filterButtonVisibility: ComplexListHeaderFilterButtonVisibilityOptions.Always,
  sortByDropdownHeaderSize: SortByDropdownHeaderSize.TranscriptOrders,
}

@Component({
  selector: 'ftr-complex-list-header',
  templateUrl: './complex-list-header.component.html',
  styleUrls: ['./complex-list-header.component.css'],
  imports: [
    ButtonComponent,
    CommonModule,
    IconComponent,
    PaginationComponent,
    ComplexListSortComponent,
    ComplexListStatusTypeComponent,
  ],
  standalone: true,
})
export class ComplexListHeaderComponent<StatusType extends string = string, SortBy extends string = string> {
  /**
   * Used for a replacement for pagination when the number of items is less than the size of the page.
   * Please provide in a singular form, e.g.to see `8 orders` provide `order`
   */
  @Input() entityName: string
  @Input() isLoading: boolean
  @Input() meta: PageMetadata | undefined
  @Input() numberOfAppliedFilters: number
  @Input() title: string
  @Input() toggled: Observable<boolean>
  @Input() sortConfig?: RadioListFilter<SortBy>
  @Input() statusTypeConfig?: RadioListFilter<StatusType>
  @Input() tabs?: FloatingTab<StatusType>[]
  @Input() sortDirectionText?: SortDirectionText
  @Input() headerSectionConfig: ComplexFilterListHeaderSectionConfiguration = DEFAULT_HEADER_SECTION_CONFIG
  /**
   * Additional buttons to display before the filter button
   *
   * Note: This doesn't do anything fancy with the breakpoint for smaller screens
   *  so if you add too much content here you'll need to adjust accordingly
   */
  @Input() additionalButtonsTemplate: TemplateRef<any> | null = null

  @Output() filterToggled = new EventEmitter<boolean>()

  readonly buttonColor = ButtonColor
  readonly badgeType = BadgeType

  toggleFilters(value: boolean): void {
    this.filterToggled.emit(!value)
  }
}
